import type { MutationFunctionOptions } from '@apollo/client';
import { useMutation } from '@apollo/client';
import type { Form } from '@aurora/shared-generated/types/graphql-schema-types';
import { PolicyHostLocation } from '@aurora/shared-generated/types/graphql-schema-types';
import type {
  ExternalCommunityGuideLineUrlQuery,
  ExternalCommunityGuideLineUrlQueryVariables,
  ExternalPrivacyPolicyUrlQuery,
  ExternalPrivacyPolicyUrlQueryVariables,
  RegisterInvitedUserMutation,
  RegisterInvitedUserMutationVariables,
  RegisterUserErrorFragment,
  RegisterUserMutation,
  RegisterUserMutationVariables,
  RegistrationUserManagementPropertiesQuery,
  RegistrationUserManagementPropertiesQueryVariables,
  UserCustomFieldsForEntityCreateQuery,
  UserCustomFieldsForEntityCreateQueryVariables
} from '@aurora/shared-generated/types/graphql-types';
import { EndUserPages, EndUserQueryParams } from '@aurora/shared-types/pages/enums';
import { enumFromValue } from '@aurora/shared-utils/helpers/objects/ObjectHelper';
import { getLog } from '@aurora/shared-utils/log';
import NextLink from 'next/link';
import React, { useContext } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import type { UseFormSetError } from 'react-hook-form';
import { SharedComponent } from '../../../enums';
import { fixMutationCallbackError } from '../../../helpers/apollo/ApolloHelper';
import { getCustomFieldInputs } from '../../../helpers/custom/CustomFieldHelper';
import FormBuilder from '../../../helpers/form/FormBuilder/FormBuilder';
import type FormFeedbackTypes from '../../../helpers/form/FormFieldFeedback/form-feedback-types';
import { diffYear } from '../../../helpers/forms/DateHelper/DateHelper';
import type { PageAndEmptyParams } from '../../../routes/endUserRoutes';
import useEndUserRoutes from '../../../routes/useEndUserRoutes';
import { LoadingButtonVariant } from '../../common/Button/enums';
import type { AppContextInterface } from '../../context/AppContext/AppContext';
import AppContext from '../../context/AppContext/AppContext';
import { FormActionButtonsPosition } from '../../form/enums';
import InputEditForm from '../../form/InputEditForm/InputEditForm';
import type { FormSpec } from '../../form/types';
import type { MutationErrorFeedback } from '../../form/useMutationErrorsHandler/useMutationErrorsHandler';
import useMutationErrorsHandler, {
  MutationErrorType
} from '../../form/useMutationErrorsHandler/useMutationErrorsHandler';
import useLocale from '../../useLocale';
import useQueryWithTracing from '../../useQueryWithTracing';
import useTranslation from '../../useTranslation';
import externalCommunityGuideLineUrl from './ExternalCommunityGuideLineUrl.query.graphql';
import externalPrivacyPolicyUrl from './ExternalPrivacyPolicyUrl.query.graphql';
import registerInvitedUserMutation from './RegisterInvitedUser.mutation.graphql';
import registerUserMutation from './RegisterUser.mutation.graphql';
import formSchema from './RegistrationForm.form.json';
import localStyles from './RegistrationForm.module.pcss';
import customFormFieldsForEntityCreateQuery from './UserCustomFieldsForEntityCreate.query.graphql';
import userManagementPropertiesQuery from './UserManagementProperties.query.graphql';

const log = getLog(module);

export interface RegistrationFormData {
  /**
   * The user's login
   */
  login: string;
  /**
   * The user's email
   */
  email: string;
  /**
   * The user's password
   */
  password: string;
  /**
   * Whether the user accepts the ToS
   */
  userAcceptsTermsOfService: boolean;
  /**
   * Whether to remember the user's password
   */
  rememberPassword: boolean;
  /**
   * Whether the user confirms the minimum age requirement for the community
   */
  userConfirmsMinAge: boolean;
  /**
   * The user's birthday
   */
  birthday: string;
  /**
   * custom fields
   */
  custom: Record<string, unknown>;
}

/**
 * Callback function after registration.
 *
 * @callback
 */
export type OnRegistrationCallback = (formData: RegistrationFormData, inviteToken: string) => void;

interface Props {
  /**
   * Callback function after registration.
   *
   * @callback
   */
  onRegistration: OnRegistrationCallback;

  /**
   * Whether ReCaptcha should be used.
   */
  useReCaptcha?: boolean;
}

/**
 * Displays a registration form.
 *
 * @author Stephen McLaughry
 */
const RegistrationForm: React.FC<React.PropsWithChildren<Props>> = ({
  onRegistration,
  useReCaptcha = true,
  children
}) => {
  const i18n = useTranslation(SharedComponent.REGISTRATION_FORM);
  const { formatMessage, loading: textLoading, FormattedMessage } = i18n;
  const cx = useClassNameMapper(localStyles);
  const formId = 'RegistrationForm';
  const handleFormSubmitMutationErrorStates = useMutationErrorsHandler(
    formId,
    i18n,
    formSchema.layout
  );

  const { data: userManagementData, loading: userManagementDataLoading } = useQueryWithTracing<
    RegistrationUserManagementPropertiesQuery,
    RegistrationUserManagementPropertiesQueryVariables
  >(module, userManagementPropertiesQuery, {
    onError: error => log.error(error, 'Error fetching user management data')
  });

  const {
    data: customFormFieldsForEntityCreateData,
    loading: customFormFieldsForEntityCreateDataLoading
  } = useQueryWithTracing<
    UserCustomFieldsForEntityCreateQuery,
    UserCustomFieldsForEntityCreateQueryVariables
  >(module, customFormFieldsForEntityCreateQuery, {
    variables: {
      useCustomFieldsForCreate: true
    },
    onError: error => log.error(error, 'Error fetching user management data')
  });

  const [registerUser] = useMutation<RegisterUserMutation, RegisterUserMutationVariables>(
    registerUserMutation
  );

  const [registerInvitedUser] = useMutation<
    RegisterInvitedUserMutation,
    RegisterInvitedUserMutationVariables
  >(registerInvitedUserMutation);
  const { router, Link } = useEndUserRoutes();
  const { authUser } = useContext<AppContextInterface>(AppContext);

  const inviteToken = router.getUnwrappedQueryParam(EndUserQueryParams.INVITE_TOKEN);

  const locale = useLocale();

  const { data: externalPrivacyPolicyUrlData, loading: externalPrivacyPolicyUrlDataLoading } =
    useQueryWithTracing<ExternalPrivacyPolicyUrlQuery, ExternalPrivacyPolicyUrlQueryVariables>(
      module,
      externalPrivacyPolicyUrl,
      {
        variables: {
          locale: locale
        }
      }
    );

  const {
    data: externalCommunityGuideLineUrlData,
    loading: externalCommunityGuideLineUrlDataLoading
  } = useQueryWithTracing<
    ExternalCommunityGuideLineUrlQuery,
    ExternalCommunityGuideLineUrlQueryVariables
  >(module, externalCommunityGuideLineUrl, {
    variables: {
      locale: locale
    }
  });

  if (
    textLoading ||
    userManagementDataLoading ||
    customFormFieldsForEntityCreateDataLoading ||
    externalPrivacyPolicyUrlDataLoading ||
    externalCommunityGuideLineUrlDataLoading
  ) {
    return null;
  }

  const {
    community: {
      userManagementProperties: {
        confirmBirthDateForCoppa,
        useTermsOfService,
        usePrivacyPolicy,
        privacyPolicyAcceptanceRequired,
        privacyPolicyHostLocation,
        useCommunityGuideLines,
        communityGuideLinesAcceptanceRequired,
        communityGuideLinesHostLocation,
        showBirthDateOnRegister,
        useBirthDateForCoppa,
        minimumRegistrationAge,
        rememberPassword: rememberPasswordValue
      }
    }
  } = userManagementData;

  const { customFormFieldsForEntityCreate } = customFormFieldsForEntityCreateData;

  const mutationErrorFeedback: MutationErrorFeedback = {
    type: MutationErrorType.FEEDBACK,
    message: formatMessage(`${formId}.General.error`)
  };

  /**
   * Helper function to get userAcceptVariant & acknowledgementVariant based on the configuration in the admin app
   */
  const getVariants = () => {
    const terms = useTermsOfService ? 'terms' : null;
    const policyAccept = usePrivacyPolicy && privacyPolicyAcceptanceRequired ? 'policy' : null;
    const guidelineAccept =
      useCommunityGuideLines && communityGuideLinesAcceptanceRequired ? 'guideline' : null;

    const userAcceptVariant =
      [terms, policyAccept, guidelineAccept].filter(Boolean).join('_') || null;

    const policyAcknowledge =
      usePrivacyPolicy && !privacyPolicyAcceptanceRequired ? 'policy' : null;
    const guidelineAcknowledge =
      useCommunityGuideLines && !communityGuideLinesAcceptanceRequired ? 'guideline' : null;

    const acknowledgementVariant =
      [policyAcknowledge, guidelineAcknowledge].filter(Boolean).join('_') || null;

    return [userAcceptVariant, acknowledgementVariant];
  };

  const [userAcceptVariant, acknowledgementVariant] = getVariants();

  /**
   * Invokes the register user mutation and subsequently displays errors or invokes the onRegistration callback
   *
   * @param formData the register user form data
   * @param setError sets errors for particular field(s)
   * @param setFormFeedback sets general feedback inside the form
   */
  async function handleRegisterUser(
    formData: RegistrationFormData,
    setError: UseFormSetError<RegistrationFormData>,
    setFormFeedback: (feedback: FormFeedbackTypes.FormFeedback) => void
  ): Promise<void> {
    const {
      login,
      email,
      password,
      userAcceptsTermsOfService,
      rememberPassword,
      userConfirmsMinAge,
      birthday,
      custom
    } = formData;
    const customFieldInputs = custom ? getCustomFieldInputs(custom) : [];
    const mutationOptions: MutationFunctionOptions<
      RegisterUserMutation,
      RegisterUserMutationVariables
    > = {
      variables: {
        registerInput: {
          login,
          email,
          password,
          ...(userAcceptVariant && { userAcceptsTermsOfService }),
          rememberPassword,
          ...(confirmBirthDateForCoppa && { userConfirmsMinAge }),
          ...(showBirthDateOnRegister && { birthday: new Date(birthday) }),
          customFields: customFieldInputs
        }
      }
    };

    const { data, errors } = await fixMutationCallbackError(registerUser)(mutationOptions);
    if (errors?.length > 0 || data?.registerUser?.errors?.length > 0) {
      handleFormSubmitMutationErrorStates<RegistrationFormData, RegisterUserErrorFragment>(
        errors,
        data?.registerUser?.errors,
        setError,
        setFormFeedback,
        mutationErrorFeedback
      );
    } else {
      onRegistration(formData, inviteToken);
    }
  }

  /**
   * Invokes the register user mutation and subsequently displays errors or invokes the onRegistration callback
   *
   * @param formData the register user form data
   * @param setError sets errors for particular field(s)
   * @param setFormFeedback sets general feedback inside the form
   */
  async function handleRegisterInvitedUser(
    formData: RegistrationFormData,
    setError: UseFormSetError<RegistrationFormData>,
    setFormFeedback: (feedback: FormFeedbackTypes.FormFeedback) => void
  ): Promise<void> {
    const {
      login,
      password,
      userAcceptsTermsOfService,
      rememberPassword,
      userConfirmsMinAge,
      birthday,
      custom
    } = formData;
    const customFieldInputs = custom ? getCustomFieldInputs(custom) : [];
    const mutationOptions: MutationFunctionOptions<
      RegisterInvitedUserMutation,
      RegisterInvitedUserMutationVariables
    > = {
      variables: {
        registerInput: {
          login,
          password,
          ...(userAcceptVariant && { userAcceptsTermsOfService }),
          rememberPassword,
          ...(confirmBirthDateForCoppa && { userConfirmsMinAge }),
          ...(showBirthDateOnRegister && { birthday: new Date(birthday) }),
          inviteToken,
          customFields: customFieldInputs
        }
      }
    };

    const { data, errors } = await fixMutationCallbackError(registerInvitedUser)(mutationOptions);
    if (errors?.length > 0 || data?.registerInvitedUser?.errors?.length > 0) {
      handleFormSubmitMutationErrorStates<RegistrationFormData, RegisterUserErrorFragment>(
        errors,
        data?.registerInvitedUser?.errors,
        setError,
        setFormFeedback,
        mutationErrorFeedback
      );
    } else {
      onRegistration(formData, inviteToken);
    }
  }

  /**
   * Runs frontend validation on the form data and invokes the registerUser or registerInvitedUser mutation depending
   * on whether an invite token is present in the URL
   *
   * @param formData the registration form data
   * @param action the form action
   * @param event the form event
   * @param setError react-hook-form setError
   * @param setFormFeedback sets inline feedback in the form
   */
  async function onSubmit(
    formData: RegistrationFormData,
    action: string,
    event: React.FormEvent<HTMLFormElement>,
    setError: UseFormSetError<RegistrationFormData>,
    setFormFeedback: (feedback: FormFeedbackTypes.FormFeedback) => void
  ): Promise<void> {
    if (
      useBirthDateForCoppa &&
      showBirthDateOnRegister &&
      diffYear(new Date(formData.birthday)) < minimumRegistrationAge
    ) {
      setError('birthday', {
        type: 'manual',
        message: formatMessage(`${formId}.birthday.validate.minRequiredYear.error`, {
          age: minimumRegistrationAge,
          shouldFocus: true
        })
      });
    } else {
      log.trace('ssoId: %O', authUser?.ssoId);
      if (!inviteToken) {
        await handleRegisterUser(formData, setError, setFormFeedback);
      } else {
        await handleRegisterInvitedUser(formData, setError, setFormFeedback);
      }
    }
  }

  /**
   * Function that helps render either the end user router link or next router link based on the host location
   *
   * @param route -  End user page route
   * @param chunk - content that has to render inside the link
   * @param hostLocation - hostLocation
   */
  const renderLink = (route: EndUserPages, chunk: string, hostLocation: PolicyHostLocation) => {
    const testIdMapping = {
      [EndUserPages.TermsOfServicePage]: `${formId}.termsOfServiceLink`,
      [EndUserPages.PrivacyPolicyPage]: `${formId}.privacyPolicyLink`,
      [EndUserPages.CommunityGuideLinesPage]: `${formId}.communityGuideLineLink`
    };

    const anchorTag = (
      <a
        data-testid={testIdMapping[route]}
        target="_blank"
        className={cx('lia-g-inline-icon-link')}
      >
        {chunk}
      </a>
    );

    const externalUrlMapping = {
      [EndUserPages.PrivacyPolicyPage]:
        externalPrivacyPolicyUrlData?.privacyPolicyForLanguage?.content ?? null,
      [EndUserPages.CommunityGuideLinesPage]:
        externalCommunityGuideLineUrlData?.communityGuideLinesForLanguage?.content ?? null
    };
    const externalUrl = externalUrlMapping[route];

    if (hostLocation === PolicyHostLocation.External && externalUrl) {
      return (
        <NextLink href={externalUrl} passHref legacyBehavior={true} prefetch={false}>
          {anchorTag}
        </NextLink>
      );
    }
    return (
      <Link<PageAndEmptyParams> route={route} legacyBehavior={true}>
        {anchorTag}
      </Link>
    );
  };

  /**
   * Render user accepts checkbox label
   */
  const renderUserAcceptsLabel = (): React.ReactNode => {
    return (
      <FormattedMessage
        id={`${formId}.userAcceptsTermsOfService.label`}
        values={{
          variant: userAcceptVariant,
          TermsOfServiceLink: chunk =>
            renderLink(EndUserPages.TermsOfServicePage, chunk, PolicyHostLocation.Internal),
          PrivacyPolicyLink: chunk =>
            renderLink(
              EndUserPages.PrivacyPolicyPage,
              chunk,
              enumFromValue(PolicyHostLocation, privacyPolicyHostLocation)
            ),
          CommunityGuideLineLink: chunk =>
            renderLink(
              EndUserPages.CommunityGuideLinesPage,
              chunk,
              enumFromValue(PolicyHostLocation, communityGuideLinesHostLocation)
            )
        }}
      />
    );
  };

  /**
   * Render acknowledgement message
   */
  const renderAcknowledgement = (): React.ReactNode => {
    return (
      <div
        data-testid={`${formId}.userAcknowledgement`}
        className={cx('lia-g-text-xs lia-g-mt-10')}
      >
        <FormattedMessage
          id={`${formId}.userAcknowledgement`}
          values={{
            variant: acknowledgementVariant,
            PrivacyPolicyLink: chunk =>
              renderLink(
                EndUserPages.PrivacyPolicyPage,
                chunk,
                enumFromValue(PolicyHostLocation, privacyPolicyHostLocation)
              ),
            CommunityGuideLineLink: chunk =>
              renderLink(
                EndUserPages.CommunityGuideLinesPage,
                chunk,
                enumFromValue(PolicyHostLocation, communityGuideLinesHostLocation)
              )
          }}
        />
      </div>
    );
  };

  const formBuilder = new FormBuilder<RegistrationFormData>(
    formId,
    i18n,
    {
      schema: formSchema as Form,
      cx
    },
    {
      useReCaptcha
    }
  );

  // noinspection TypeScriptValidateTypes
  formBuilder
    .addTextInputField({
      name: 'login',
      ariaLabel: formatMessage('RegistrationForm.login.label'),
      validations: {
        required: true
      },
      attributes: {
        autoComplete: 'username'
      },
      formGroupSpec: {
        showInfo: true
      },
      focus: true,
      defaultValue: ''
    })
    .addTextInputField({
      name: 'email',
      validations: {
        required: true
      },
      attributes: {
        autoComplete: 'email'
      },
      defaultValue: '',
      isVisible: {
        watchFields: null,
        callback: () => inviteToken === null
      }
    })
    .addNewPasswordField({
      name: 'password',
      validations: {
        required: true
      },
      passwordViewable: true,
      defaultValue: '',
      usernameName: 'login'
    })
    .addDateInputField({
      name: 'birthday',
      attributes: {
        autoComplete: 'birthday'
      },
      isVisible: {
        watchFields: null,
        callback: () => showBirthDateOnRegister
      },
      defaultValue: null
    })
    .addCheckField({
      name: 'userConfirmsMinAge',
      label: formatMessage(`${formId}.userConfirmsMinAge.label`, {
        age: minimumRegistrationAge
      }),
      validations: {
        required: formatMessage(`${formId}.userConfirmsMinAge.required.error`, {
          age: minimumRegistrationAge
        })
      },
      isVisible: {
        watchFields: null,
        callback: () => confirmBirthDateForCoppa
      },
      defaultValue: false
    })
    .addCheckField({ name: 'rememberPassword', defaultValue: rememberPasswordValue })
    .addCheckField({
      checkFieldInputClassName: cx('lia-checkbox'),
      name: 'userAcceptsTermsOfService',
      label: renderUserAcceptsLabel(),
      validations: {
        required: formatMessage(`${formId}.userAcceptsTermsOfService.required.error`, {
          variant: userAcceptVariant
        })
      },
      isVisible: {
        watchFields: null,
        callback: () => !!userAcceptVariant
      },
      defaultValue: false
    })
    .addEntityCustomFormFields(customFormFieldsForEntityCreate);

  // noinspection TypeScriptValidateTypes
  const formSpec: FormSpec<RegistrationFormData> = formBuilder
    .setActionButtonsPosition(FormActionButtonsPosition.STRETCH)
    .addSubmitAction({ buttonType: LoadingButtonVariant.LOADING_BUTTON, size: 'lg' })
    .build();

  return (
    <InputEditForm<RegistrationFormData> formSpec={formSpec} onSubmit={onSubmit}>
      {acknowledgementVariant && renderAcknowledgement()}
      {children}
    </InputEditForm>
  );
};

export default RegistrationForm;
